import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import type { AdminProfile } from "@/types/api";

/**
 * Who is signed in, and what they may do.
 *
 * The only hand-written slice in the app — everything else is server state
 * owned by RTK Query. It is populated from `GET /v1/admin/me`, never decoded
 * from the JWT: the server resolves permissions from the database on every
 * request precisely so a revoked role takes effect immediately, and reading
 * them from a token claim here would reintroduce the staleness that design
 * avoids.
 *
 * It holds the few facts that gate the *shell* — the name in the top bar, the
 * permissions the nav filters on, whether this person owns the installation.
 * Everything else about the account stays in the `/me` query where it belongs;
 * copying the whole profile in here would give the app two versions of it that
 * drift apart the moment one is saved.
 */
export interface SessionState {
  /**
   * `unavailable` is not `signed-out`, and the difference decides what the
   * user sees. Signed out means the token is gone and the sign-in screen is
   * the answer. Unavailable means the token is fine and the *server* did not
   * answer — sending someone to sign in again over that is telling them to fix
   * something that is not broken, and they would land straight back here.
   */
  status: "unknown" | "signed-out" | "signed-in" | "unavailable";
  admin_id: string | null;
  email: string | null;
  name: string | null;
  avatar_url: string | null;
  /**
   * Owner-only controls key off this rather than off a permission. Account
   * provisioning is deliberately not grantable: a role that could confer it
   * would be a path to manufacturing a second owner.
   */
  is_owner: boolean;
  permissions: string[];
  role_keys: string[];
  /** Role display names, for the line under the name in the account menu. */
  role_names: string[];
}

const initialState: SessionState = {
  status: "unknown",
  admin_id: null,
  email: null,
  name: null,
  avatar_url: null,
  is_owner: false,
  permissions: [],
  role_keys: [],
  role_names: [],
};

const sessionSlice = createSlice({
  name: "session",
  initialState,
  reducers: {
    /**
     * Takes the `/me` payload whole rather than a hand-assembled object, so a
     * field added to the profile cannot be silently dropped on the way in.
     */
    sessionEstablished(state, action: PayloadAction<AdminProfile>) {
      const profile = action.payload;
      state.status = "signed-in";
      state.admin_id = profile.id;
      state.email = profile.email;
      state.name = profile.name;
      state.avatar_url = profile.avatar_url;
      state.is_owner = profile.is_owner;
      state.permissions = profile.permissions;
      state.role_keys = profile.role_keys;
      state.role_names = profile.roles.map((role) => role.name);
    },

    /**
     * The session could not be loaded, but there is still a token.
     *
     * Permissions stay empty, which is the honest answer — we do not know what
     * this person may do, and guessing in either direction is worse than the
     * shell saying it cannot load right now.
     */
    sessionUnavailable() {
      return { ...initialState, status: "unavailable" as const };
    },

    sessionCleared() {
      // Return a fresh object rather than mutating — leaving a stale
      // permission array behind after sign-out is exactly the bug that lets a
      // signed-out shell briefly render admin controls.
      return { ...initialState, status: "signed-out" as const };
    },
  },
});

export const { sessionEstablished, sessionUnavailable, sessionCleared } = sessionSlice.actions;
export const sessionReducer = sessionSlice.reducer;
